/**
 * 表单与表头 i18n 通用解析（多页面复用）
 *
 * 使用方式：
 * 1. 在页面的 config 中：用 labelKey、attrs.placeholderKey、attrs.rangeSeparatorKey、options[].valueKey 替代中文
 * 2. 在页面 computed 中：
 *    searchFormI18n() { return resolveFormI18n(this.searchForm, 'purchasing', this.$t.bind(this)); }
 *    titleDataI18n() { return resolveTitleDataI18n(this.titleData, 'purchasing', this.$t.bind(this)); }
 * 3. 命名空间按模块：purchasing / app / dataCenter / financeCenter 等
 *
 * 表单 config 约定：
 * - labelKey: 对应 namespace.xxx 的 label 文案
 * - attrs.placeholderKey: 对应 placeholder
 * - attrs.rangeSeparatorKey: 对应日期范围「至」等（如 namespace 无此 key，可传 'app.to' 需特殊处理，或确保模块内有 to）
 * - options[].valueKey: 选项的展示文案（输出为 { key, value }）
 *
 * 表头 config 约定：
 * - 每列使用 labelKey 指向 namespace.xxx，不再写 label 中文
 */

/**
 * @param {Object} searchForm - 表单配置
 * @param {string} namespace - i18n 命名空间
 * @param {Function} t - this.$t.bind(this)
 * @returns {Object} 解析后的表单配置
 */
function translateWithFallback(fullKey, t) {
  const translated = t(fullKey);
  if (translated !== fullKey) return translated;

  const dotIndex = fullKey.lastIndexOf('.');
  if (dotIndex === -1) return translated;
  const prefix = fullKey.slice(0, dotIndex + 1);
  const leaf = fullKey.slice(dotIndex + 1);

  // Backward compatibility for keys like `salesCenter.案例46`.
  // If exact key is missing, retry with trailing digits removed.
  if (/[\u4e00-\u9fff]/.test(leaf) && /\d+$/.test(leaf)) {
    const leafWithoutSuffix = leaf.replace(/\d+$/, '');
    if (leafWithoutSuffix && leafWithoutSuffix !== leaf) {
      const fallbackKey = prefix + leafWithoutSuffix;
      const fallbackTranslated = t(fallbackKey);
      if (fallbackTranslated !== fallbackKey) return fallbackTranslated;
    }
  }
  return translated;
}

function resolveKey(key, namespace, t) {
  if (!key) return '';
  const ns = namespace ? namespace + '.' : '';
  const fullKey = key.includes('.') ? key : (ns + key);
  return translateWithFallback(fullKey, t);
}

export function resolveFormI18n(searchForm, namespace, t) {
  const cfg = {};
  Object.keys(searchForm).forEach((k) => {
    const field = searchForm[k];
    const next = { ...field };
    if (field.labelKey) next.label = resolveKey(field.labelKey, namespace, t);
    if (field.attrs) {
      next.attrs = { ...field.attrs };
      if (field.attrs.placeholderKey) next.attrs.placeholder = resolveKey(field.attrs.placeholderKey, namespace, t);
      if (field.attrs.rangeSeparatorKey) next.attrs.rangeSeparator = resolveKey(field.attrs.rangeSeparatorKey, namespace, t);
    }
    if (Array.isArray(field.options) && field.options.some((o) => o.valueKey)) {
      next.options = field.options.map((o) => (o.valueKey ? { key: o.key, value: resolveKey(o.valueKey, namespace, t) } : o));
    }
    cfg[k] = next;
  });
  return cfg;
}

/**
 * 表头列配置 i18n 通用解析
 * 将 titleData 中的 labelKey 解析为当前语言文案，供 MyTable 等使用。
 *
 * @param {Array} titleData - 列配置，每项可含 labelKey（对应 namespace.xxx）
 * @param {string} namespace - i18n 命名空间，如 'purchasing'、'dataCenter'
 * @param {Function} t - 即 this.$t.bind(this)
 * @returns {Array} 解析后的列配置
 */
export function resolveTitleDataI18n(titleData, namespace, t) {
  const list = Array.isArray(titleData) ? titleData : [];
  return list.map((col) => ({
    ...col,
    label: col.labelKey ? resolveKey(col.labelKey, namespace, t) : (col.label || col.prop)
  }));
}

/** 库存状态：后端返回状态值，用于低库存提醒比较 */
export const INVENTORY_STATUS_LIST = ['预售', '缺货', '库存紧张', 'Presale', 'Out of Stock', 'Low Stock'];

const INVENTORY_STATUS_KEY_MAP = [
  { key: 'purchasing.presale', match: ['预售', 'presale', 'pre-sale'] },
  { key: 'purchasing.outOfStock', match: ['缺货', 'outofstock', 'out of stock'] },
  { key: 'purchasing.lowStock', match: ['库存紧张', 'lowstock', 'low stock'] },
  {
    key: 'purchasing.availableStock',
    match: ['库存充足', '可用库存', 'availablestock', 'available stock', 'in stock']
  }
];

function normalizeInventoryStatus(status) {
  return String(status || '')
    .replace(/\s+/g, '')
    .replace(/[：:]/g, '')
    .trim();
}

export function formatInventoryStatus(status, t) {
  if (!status) return '';
  const normalizedStatus = normalizeInventoryStatus(status);
  const matchedKey = INVENTORY_STATUS_KEY_MAP.find(({ match }) => {
    return match.some((item) => normalizedStatus.toLowerCase().includes(item.toLowerCase()));
  })?.key;
  return matchedKey ? t(matchedKey) : String(status).trim();
}
